import React, { FC, useState } from 'react';
import { Box, TextField, CircularProgress, Alert, InputAdornment, Paper, Button } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { OperatorQuery } from '../../../services/query';
import { OperatorCommand } from '../../../services/command';
import Swal from 'sweetalert2';

interface User {
    userRef: string;
    userName: string;
    fullName?: string | null;
    email?: string;
    hrref?: string;
    isActive?: boolean;
    applicationUserId?: number;
}

interface IProps {
    roleId: number;
    onRefresh?: () => void;
    onClose?: () => void;
}

export const SCUIOperatorSearch: FC<IProps> = ({ roleId, onRefresh, onClose }) => {
    const { t } = useTranslation();
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showWarning, setShowWarning] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

    const fetchUsers = async (searchQuery: string) => {
        if (!searchQuery || searchQuery.length < 3) {
            setAllUsers([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            // State'i önce temizle
            setAllUsers([]);

            const params = {
                username: searchQuery,
                status: 'all',
            };

            const response = await OperatorQuery.GetOperatorFindByUserName(params);

            if (response?.model?.data && Array.isArray(response.model.data)) {
                const users: User[] = response.model.data.map((user: any) => ({
                    userRef: user.userRef?.toString() || user.applicationUserId?.toString(),
                    userName: user.username,
                    fullName: user.fullName,
                    email: user.email,
                    hrref: user.hrref,
                    isActive: user.isActive,
                    applicationUserId: user.applicationUserId,
                }));

                setAllUsers(users);
            } else {
                setAllUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setAllUsers([]);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleAddUser = async (user: User) => {
        try {
            const params = {
                userId: Number(user.applicationUserId || user.userRef),
                roleId: Number(roleId),
            };

            await OperatorCommand.PostAddOperatorToRole(params);

            Swal.fire({
                title: t('general.success'),
                text: t('general.added'),
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
            });

            // Modal'ı kapat ve refresh at
            if (onClose) {
                onClose();
            }

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error adding user to role:', error);
            Swal.fire({
                title: t('general.error'),
                text: t('general.couldntDeleted'),
                icon: 'error',
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (searchTerm.length >= 3) {
                fetchUsers(searchTerm.trim());
            }
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder={t('general.userName')}
                    value={searchTerm}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchTerm(newValue);

                        if (searchTimeout) {
                            clearTimeout(searchTimeout);
                        }

                        if (newValue.length >= 3) {
                            setShowWarning(false);
                            const timeout = setTimeout(() => {
                                fetchUsers(newValue.trim());
                            }, 500);
                            setSearchTimeout(timeout);
                        } else if (newValue.length > 0) {
                            setShowWarning(true);
                            setAllUsers([]);
                        } else {
                            setShowWarning(false);
                            setAllUsers([]);
                        }
                    }}
                    onKeyDown={handleKeyPress}
                    variant="outlined"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: isLoadingUsers && (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {showWarning && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {t('roleManagement.minCharacterWarning')}
                    </Alert>
                )}
            </Box>

            {allUsers.length > 0 && (
                <Paper sx={{ width: '100%', mt: 2 }}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">{t('general.userName')}</TableCell>
                                    <TableCell align="center">{t('general.email')}</TableCell>
                                    <TableCell align="center">{t('general.add')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allUsers.map((user) => (
                                    <TableRow key={user.userRef} sx={{ '& > *': { borderBottom: 'unset' } }}>
                                        <TableCell align="center">{user.userName}</TableCell>
                                        <TableCell align="center">{user.email || '-'}</TableCell>
                                        <TableCell align="center">
                                            <Button onClick={() => handleAddUser(user)}>
                                                <AddIcon />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};
